import { useEffect, useState, type ReactNode } from 'react'
import { Box, Drawer, IconButton, Link, Stack, Typography, useTheme } from '@mui/material'
import { ArrowUpRightIcon, PencilIcon, XIcon } from 'lucide-react'
import type { ContentIdea, ContentIdeaStatus } from '@repo/types'
import { Button, EMPTY_VALUE, Input, Skeleton } from '@repo/ui'

import { SelectField, StatusChip } from 'components'
import { CONTENT_IDEA_STATES } from 'constants/statuses'
import { useSnackbar } from 'contexts'
import {
  useAddContentIdeaSource,
  useContentIdea,
  useContentIdeaCorpus,
  useRemoveContentIdeaSource,
  useSetContentIdeaStatus,
} from 'hooks/api'

import { formatIsoDay } from '../GtmTradeShowsPage/tradeShowModel'
import {
  ARTICLE_OPTIONS,
  articleFor,
  carriedBy,
  isCorpusFed,
  industryLabel,
  isSeoFed,
  MANAGE_IDEAS_TIP,
  originLabel,
  scoreMethod,
  scoreText,
  SEO_RECOMMENDATIONS_HREF,
  solutionLabel,
  statusActions,
  typeLabel,
  type StatusAction,
} from './contentIdeaModel'
import { RecommendationCard } from './RecommendationCard'

const DRAWER_WIDTH = 600
const URL_RE = /^https?:\/\/\S+$/i

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <Stack spacing={2} component="section" aria-label={title}>
    <Typography variant="body2" sx={{ fontWeight: 600 }}>
      {title}
    </Typography>
    {children}
  </Stack>
)
const Small = ({ children, muted }: { children: ReactNode; muted?: boolean }) => (
  <Typography
    variant="body3"
    component="div"
    color={muted ? 'text.secondary' : undefined}
    sx={{ lineHeight: 1.55, whiteSpace: 'pre-wrap' }}
  >
    {children}
  </Typography>
)
const OutLink = ({ href, children }: { href: string; children: ReactNode }) => (
  <Link
    href={href}
    target="_blank"
    rel="noreferrer noopener"
    color="inherit"
    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
  >
    {children}
    <ArrowUpRightIcon strokeWidth={1} size={14} style={{ opacity: 0.6 }} />
  </Link>
)
const day = (iso: string | null | undefined) => (iso ? formatIsoDay(iso.slice(0, 10)) : '')

/**
 * VRG-740 — one content idea: why it is worth writing, the terms it targets (with what the keyword
 * master says about them), the research behind it, where it stands and who moved it. Status moves
 * are the actions at the top; Reject asks for a reason, Mark published asks which article. View for
 * GTM staff; every change needs 'Manage ideas' (server-enforced; disabled here with the reason, §27).
 */
export const ContentIdeaDrawer = ({
  ideaId,
  canEdit,
  onEdit,
  onClose,
}: {
  ideaId: string | null
  canEdit: boolean
  onEdit: (idea: ContentIdea) => void
  onClose: () => void
}) => {
  const theme = useTheme()
  const { open: notify } = useSnackbar()
  const { data: idea, isLoading } = useContentIdea(ideaId)
  const move = useSetContentIdeaStatus()
  const addSource = useAddContentIdeaSource()
  const removeSource = useRemoveContentIdeaSource()
  const [pending, setPending] = useState<StatusAction | null>(null)
  const [reason, setReason] = useState('')
  const [article, setArticle] = useState('')
  const [research, setResearch] = useState('')
  const isUrl = URL_RE.test(research.trim())
  const { data: corpus } = useContentIdeaCorpus(isUrl ? '' : research)

  useEffect(() => {
    setPending(null)
    setReason('')
    setArticle('')
    setResearch('')
  }, [ideaId])

  const fail = (e: unknown) => notify({ type: 'error', text: (e as Error).message })

  const doMove = (
    to: ContentIdeaStatus,
    extra: {
      note?: string | null
      published_section?: 'insights' | 'learn'
      published_slug?: string
    } = {},
  ) => {
    if (!idea) return
    move.mutate(
      { id: idea.id, input: { to, ...extra } },
      {
        onSuccess: () => {
          notify({
            type: 'success',
            text: `Moved “${idea.title}” to ${CONTENT_IDEA_STATES[to].label.toLowerCase()}.`,
          })
          setPending(null)
          setReason('')
        },
        onError: fail,
      },
    )
  }

  const onAction = (a: StatusAction) => {
    if (!a.needs) return doMove(a.to)
    setPending(a)
    if (a.needs === 'article' && idea?.published_slug) setArticle(`${idea.published_section}/${idea.published_slug}`)
  }

  const addResearch = (input: { market_item_id?: string; url?: string }) => {
    if (!idea) return
    addSource.mutate(
      { id: idea.id, input },
      {
        onSuccess: () => {
          setResearch('')
          notify({ type: 'success', text: 'Research link added.' })
        },
        onError: fail,
      },
    )
  }

  const linked = idea ? articleFor(idea) : null
  const method = idea ? scoreMethod(idea.score_basis) : null
  const attached = new Set(idea?.sources.map((s) => s.market_item_id) ?? [])

  return (
    <Drawer
      anchor="right"
      open={!!ideaId}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          margin: 4,
          borderRadius: 3,
          maxHeight: 'calc(100vh - 32px)',
          minWidth: DRAWER_WIDTH,
          maxWidth: DRAWER_WIDTH,
          display: 'flex',
          flexDirection: 'column',
          backgroundImage: 'none',
          backgroundColor: 'background.paper',
        },
      }}
    >
      {!idea ? (
        <Stack sx={{ p: 6 }} spacing={3}>
          {isLoading || ideaId ? (
            <>
              <Skeleton height={24} width="70%" />
              <Skeleton height={16} width="40%" />
              <Skeleton height={120} />
            </>
          ) : null}
        </Stack>
      ) : (
        <>
          <Stack sx={{ p: 6, pb: 4, flexShrink: 0 }} spacing={3}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={3}>
              <Stack spacing={1} sx={{ minWidth: 0 }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {idea.title}
                </Typography>
                <Typography variant="body3" color="text.secondary">
                  {typeLabel(idea.type)} · {originLabel(idea.origin)}
                  {idea.priority ? ` · ${idea.priority}` : ''}
                  {scoreText(idea.score) ? ` · score ${scoreText(idea.score)}` : ''}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={3} sx={{ flexShrink: 0 }}>
                <Button
                  variant="secondary"
                  startIcon={<PencilIcon size={18} strokeWidth={1} />}
                  disabled={!canEdit}
                  disabledReason={canEdit ? undefined : MANAGE_IDEAS_TIP}
                  onClick={() => onEdit(idea)}
                >
                  Edit
                </Button>
                <IconButton onClick={onClose} aria-label="Close idea" sx={{ color: theme.palette.text.primary }}>
                  <XIcon strokeWidth={1} />
                </IconButton>
              </Stack>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" useFlexGap>
              <StatusChip statuses={CONTENT_IDEA_STATES} status={idea.status} size="small" />
              <Typography variant="body4" color="text.secondary">
                since {day(idea.status_changed_at)}
                {idea.status_changed_by ? ` by ${idea.status_changed_by}` : ''}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
              {/* VRG-741: an idea with an undecided recommendation card is decided ON the card
                  (approve to write / reject), so the generic forward move is not offered twice. */}
              {(idea.recommendation && idea.status === 'idea' ? [] : statusActions(idea.status)).map((a) => (
                <Button
                  key={a.to}
                  variant={a.to === 'rejected' ? 'secondary' : 'primary'}
                  disabled={!canEdit || move.isPending}
                  disabledReason={canEdit ? undefined : MANAGE_IDEAS_TIP}
                  onClick={() => onAction(a)}
                >
                  {a.label}
                </Button>
              ))}
            </Stack>
            {pending?.needs === 'reason' ? (
              <Stack spacing={2}>
                <Input label="Why not (optional)" value={reason} onChange={setReason} multiline minRows={2} />
                <Stack direction="row" spacing={3}>
                  <Button variant="secondary" onClick={() => setPending(null)} disabled={move.isPending}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    loading={move.isPending}
                    onClick={() => doMove('rejected', { note: reason.trim() || null })}
                  >
                    Reject idea
                  </Button>
                </Stack>
              </Stack>
            ) : null}
            {pending?.needs === 'article' ? (
              <Stack spacing={2}>
                <SelectField
                  label="Published as"
                  search
                  options={ARTICLE_OPTIONS}
                  value={article}
                  placeholder="Pick the article"
                  onChange={(v) => setArticle(v as string)}
                />
                <Stack direction="row" spacing={3}>
                  <Button variant="secondary" onClick={() => setPending(null)} disabled={move.isPending}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    loading={move.isPending}
                    disabled={!article}
                    onClick={() => {
                      const [section, slug] = article.split('/')
                      doMove('published', {
                        published_section: section as 'insights' | 'learn',
                        published_slug: slug,
                      })
                    }}
                  >
                    Mark published
                  </Button>
                </Stack>
              </Stack>
            ) : null}
          </Stack>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              px: 6,
              pb: 6,
            }}
          >
            <Stack spacing={5}>
              {idea.recommendation ? <RecommendationCard idea={idea} canEdit={canEdit} /> : null}
              <Section title="Why write it">
                <Small muted={!idea.rationale}>{idea.rationale ?? 'No rationale yet.'}</Small>
                {isSeoFed(idea.origin) ? (
                  <Small muted>
                    Fed from <OutLink href={SEO_RECOMMENDATIONS_HREF}>SEO Recommendations</OutLink>
                    {idea.origin_ref ? ` (${idea.origin_ref})` : ''}.
                  </Small>
                ) : isCorpusFed(idea.origin, idea.origin_ref) ? (
                  <Small muted>Fed from the research corpus (Content → Library) by the corpus feed.</Small>
                ) : idea.origin_ref ? (
                  <Small muted>From {idea.origin_ref}.</Small>
                ) : null}
                {carriedBy(idea.score_basis).length ? (
                  <Small muted>Score carried by: {carriedBy(idea.score_basis).join(', ')}.</Small>
                ) : null}
                {method ? <Small muted>Score: {method}</Small> : null}
              </Section>

              <Section title={`Target terms (${idea.terms.length})`}>
                {idea.terms.length ? (
                  <Stack spacing={1.5}>
                    {idea.terms.map((t) => (
                      <Stack key={t.id} direction="row" justifyContent="space-between" spacing={3}>
                        <Typography variant="body3" sx={{ fontWeight: t.is_primary ? 600 : 400 }}>
                          {t.term}
                        </Typography>
                        <Typography variant="body4" color="text.secondary" noWrap>
                          {t.keyword_id
                            ? [
                                t.keyword_priority,
                                t.cluster,
                                t.intent,
                                t.keyword_target_url ? `page ${t.keyword_target_url}` : null,
                              ]
                                .filter(Boolean)
                                .join(' · ') || 'In the keyword master'
                            : 'Not in the keyword master'}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                ) : (
                  <Small muted>No terms yet.</Small>
                )}
              </Section>

              <Section title="Solutions and industries">
                <Small muted={!idea.solutions.length && !idea.industries.length}>
                  {[...idea.solutions.map(solutionLabel), ...idea.industries.map(industryLabel)].join(' · ') ||
                    'Not tagged yet.'}
                </Small>
              </Section>

              <Section title={`Research (${idea.sources.length})`}>
                {idea.sources.map((s) => (
                  <Stack
                    key={s.market_item_id}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={3}
                  >
                    <Stack sx={{ minWidth: 0 }}>
                      <Typography variant="body3" noWrap title={s.title ?? s.url}>
                        <OutLink href={s.url}>{s.title ?? s.url}</OutLink>
                      </Typography>
                      <Typography variant="body4" color="text.secondary" noWrap>
                        {[s.publication, day(s.published_at), s.featured ? `featured: ${s.featured}` : null, s.note]
                          .filter(Boolean)
                          .join(' · ') || EMPTY_VALUE}
                      </Typography>
                    </Stack>
                    <IconButton
                      aria-label={`Remove ${s.title ?? s.url}`}
                      size="small"
                      disabled={!canEdit || removeSource.isPending}
                      onClick={() => removeSource.mutate({ id: idea.id, itemId: s.market_item_id }, { onError: fail })}
                    >
                      <XIcon strokeWidth={1} size={16} />
                    </IconButton>
                  </Stack>
                ))}
                {canEdit ? (
                  <Stack spacing={2}>
                    <Stack direction="row" spacing={3} alignItems="flex-end">
                      <Input
                        label="Add research"
                        value={research}
                        onChange={setResearch}
                        placeholder="Search the corpus, or paste a link"
                      />
                      {isUrl ? (
                        <Button
                          variant="secondary"
                          loading={addSource.isPending}
                          onClick={() => addResearch({ url: research.trim() })}
                        >
                          Add link
                        </Button>
                      ) : null}
                    </Stack>
                    {!isUrl && research.trim().length >= 2
                      ? (corpus ?? [])
                          .filter((c) => !attached.has(c.id))
                          .slice(0, 8)
                          .map((c) => (
                            <Stack
                              key={c.id}
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                              spacing={3}
                            >
                              <Stack sx={{ minWidth: 0 }}>
                                <Typography variant="body3" noWrap title={c.title ?? c.url}>
                                  {c.title ?? c.url}
                                </Typography>
                                <Typography variant="body4" color="text.secondary" noWrap>
                                  {[c.publication, c.solution ? solutionLabel(c.solution) : null]
                                    .filter(Boolean)
                                    .join(' · ') || c.url}
                                </Typography>
                              </Stack>
                              <Button
                                variant="secondary"
                                disabled={addSource.isPending}
                                onClick={() => addResearch({ market_item_id: c.id })}
                              >
                                Add
                              </Button>
                            </Stack>
                          ))
                      : null}
                    <Small muted>A link not in the corpus is added to it unreviewed, without fetching the page.</Small>
                  </Stack>
                ) : null}
              </Section>

              {idea.published_slug || idea.draft_url ? (
                <Section title="Article">
                  {idea.draft_url ? (
                    <Small>
                      Draft: <OutLink href={idea.draft_url}>{idea.draft_url}</OutLink>
                    </Small>
                  ) : null}
                  {idea.published_slug ? (
                    <Small>
                      {linked
                        ? `${linked.title} (${linked.path})`
                        : `/${idea.published_section}/${idea.published_slug} (not in the website snapshot)`}
                    </Small>
                  ) : null}
                </Section>
              ) : null}

              {idea.notes ? (
                <Section title="Notes">
                  <Small>{idea.notes}</Small>
                </Section>
              ) : null}

              <Section title="History">
                {idea.events.map((e) => (
                  <Small key={e.id} muted>
                    {day(e.at)} · {e.from_status ? `${CONTENT_IDEA_STATES[e.from_status].label} → ` : ''}
                    {CONTENT_IDEA_STATES[e.to_status].label}
                    {e.actor ? ` · ${e.actor}` : ''}
                    {e.note ? ` · ${e.note}` : ''}
                  </Small>
                ))}
              </Section>
            </Stack>
          </Box>
        </>
      )}
    </Drawer>
  )
}
